import React from "react";
import { useDialogFocus } from "./atoms.jsx";
import { I } from "./icons.jsx";
import "./account.css";

export const CLOSE_ACCOUNT_CONFIRMATION = "CLOSE";
export const CLOSE_ACCOUNT_COPY =
  "This signs you out, disables future sign-in under the current implementation, and closes access to your account. It does not delete your saved items, searches, alert rules, notification channels, alert history, or feedback.";

const safeErrorMessage = (error, action) => {
  if (error?.status === 400 || error?.status === 403) {
    return `${error.message || `${action} was rejected.`}${error?.requestId ? ` Reference: ${error.requestId}.` : ""}`;
  }
  if (error?.status === 429) {
    return `Too many requests. Try again in ${error.retryAfter || 60} seconds.${error?.requestId ? ` Reference: ${error.requestId}.` : ""}`;
  }
  return `${action} could not be completed.${error?.requestId ? ` Reference: ${error.requestId}.` : ""}`;
};

function CloseAccountDialog({ open, onClose, onConfirm }) {
  const [confirmation, setConfirmation] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState(null);
  const titleId = React.useId();
  const detailId = React.useId();
  const closeDialog = React.useCallback(() => {
    if (!submitting) onClose?.();
  }, [onClose, submitting]);
  const dialogRef = useDialogFocus({ enabled: open, onClose: closeDialog });
  const confirmed = confirmation.trim() === CLOSE_ACCOUNT_CONFIRMATION;

  React.useEffect(() => {
    if (!open) {
      setConfirmation("");
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  if (!open) return null;

  const submit = async (event) => {
    event.preventDefault();
    if (!confirmed || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm?.();
    } catch (nextError) {
      setError(safeErrorMessage(nextError, "Account closure"));
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="account-scrim" data-testid="close-account-scrim" onMouseDown={closeDialog}/>
      <section
        className="account-dialog"
        data-testid="close-account-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={detailId}
        tabIndex={-1}
        ref={dialogRef}
      >
        <div className="account-dialog-head">
          <div>
            <span className="account-danger-label">Account access</span>
            <h2 id={titleId}>Close account</h2>
          </div>
          <button type="button" className="btn icon sm ghost" aria-label="Cancel account closure" onClick={closeDialog} disabled={submitting}>
            <I.x size={14}/>
          </button>
        </div>
        <p id={detailId} data-testid="close-account-copy">{CLOSE_ACCOUNT_COPY}</p>
        <form onSubmit={submit}>
          <label className="lbl" htmlFor="close-account-confirmation">
            Type <span className="mono">CLOSE</span> to confirm
          </label>
          <input
            id="close-account-confirmation"
            className="input mono"
            data-testid="close-account-confirmation"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck="false"
          />
          {error ? <p className="account-form-error" data-testid="close-account-error" role="alert">{error}</p> : null}
          <div className="account-dialog-actions">
            <button type="button" className="btn" data-testid="close-account-cancel" onClick={closeDialog} disabled={submitting}>Cancel</button>
            <button type="submit" className="btn account-danger-button" data-testid="close-account-confirm" disabled={!confirmed || submitting}>
              {submitting ? "Closing…" : "Close account"}
            </button>
          </div>
        </form>
      </section>
    </>
  );
}

function AccountMenu({ user, onSignOut, onUpdateProfile, onCloseAccount }) {
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const [displayName, setDisplayName] = React.useState(user?.display_name || "");
  const [saving, setSaving] = React.useState(false);
  const [profileError, setProfileError] = React.useState(null);
  const [closeOpen, setCloseOpen] = React.useState(false);
  const triggerRef = React.useRef(null);
  const menuBlockedRef = React.useRef(false);
  menuBlockedRef.current = saving || closeOpen;
  const titleId = React.useId();
  const closeMenu = React.useCallback(() => {
    if (menuBlockedRef.current) return;
    setOpen(false);
    setEditing(false);
    setProfileError(null);
  }, []);
  const menuRef = useDialogFocus({ enabled: open, onClose: closeMenu, restoreFocusTo: triggerRef.current });

  React.useEffect(() => {
    if (!open) setDisplayName(user?.display_name || "");
  }, [open, user?.id, user?.display_name]);

  const initials = String(user?.display_name || user?.email || "Account")
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "A";

  const startEditing = () => {
    setDisplayName(user?.display_name || "");
    setProfileError(null);
    setEditing(true);
  };

  const submitProfile = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setProfileError(null);
    try {
      const updated = await onUpdateProfile?.(displayName);
      setDisplayName(updated?.display_name || "");
      setEditing(false);
    } catch (error) {
      setProfileError(safeErrorMessage(error, "Profile update"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="account-menu-wrap">
      <button
        type="button"
        className="avatar"
        data-testid="account-menu-button"
        ref={triggerRef}
        onClick={() => setOpen((value) => !value)}
        title="Account menu"
        aria-label={`Account menu for ${user?.email || "signed-in user"}`}
        aria-haspopup="dialog"
        aria-expanded={open}
      >{initials}</button>
      {open ? (
        <section
          className="account-menu"
          data-testid="account-menu"
          role="dialog"
          aria-modal="false"
          aria-labelledby={titleId}
          tabIndex={-1}
          ref={menuRef}
        >
          <div className="account-menu-heading">
            <div>
              <strong id={titleId} data-testid="account-display-name">{user?.display_name || "KaratScan account"}</strong>
              <span data-testid="account-email">{user?.email}</span>
            </div>
            <button type="button" className="btn icon sm ghost" aria-label="Close account menu" onClick={closeMenu}><I.x size={13}/></button>
          </div>

          {editing ? (
            <form className="account-profile-form" data-testid="profile-form" onSubmit={submitProfile}>
              <label className="lbl" htmlFor="profile-display-name">Display name</label>
              <input
                id="profile-display-name"
                className="input"
                data-testid="profile-display-name"
                maxLength={120}
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                disabled={saving}
              />
              {profileError ? <p className="account-form-error" data-testid="profile-error" role="alert">{profileError}</p> : null}
              <div className="account-inline-actions">
                <button type="button" className="btn xs" onClick={() => { setEditing(false); setProfileError(null); }} disabled={saving}>Cancel</button>
                <button type="submit" className="btn primary xs" data-testid="profile-save" disabled={saving}>{saving ? "Saving…" : "Save"}</button>
              </div>
            </form>
          ) : (
            <button type="button" className="btn sm" data-testid="profile-edit" onClick={startEditing}>Edit display name</button>
          )}

          <div className="account-menu-divider"/>
          <button type="button" className="btn sm" data-testid="signout-button" disabled={saving} onClick={() => { if (!saving) { setOpen(false); onSignOut?.(); } }}>Sign out</button>
          <button type="button" className="btn sm account-close-entry" data-testid="close-account-open" disabled={saving} onClick={() => { if (!saving) setCloseOpen(true); }}>Close account</button>
        </section>
      ) : null}
      <CloseAccountDialog open={closeOpen} onClose={() => setCloseOpen(false)} onConfirm={onCloseAccount}/>
    </div>
  );
}

export { AccountMenu, CloseAccountDialog, safeErrorMessage };
